import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Bookmark, FileText, Send, Mail, Download, Sparkles,
  Loader2, Check, DollarSign, Tag, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SAVE AS DELIVERABLE MODAL
 * 
 * Allows saving AI-generated content as a formal deliverable with:
 * - Title
 * - Type (notat, kontrakt, brev, etc.)
 * - Value receipt (verdi levert description)
 * - Delivery channel (save, email, portal)
 */
const SaveDeliverableModal = ({
  isOpen,
  onClose,
  messageId,
  content,
  caseId,
  caseName,
  onSaved
}) => {
  const [title, setTitle] = useState('');
  const [deliverableType, setDeliverableType] = useState('note');
  const [valueReceipt, setValueReceipt] = useState('');
  const [deliveryChannel, setDeliveryChannel] = useState('save');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [generatingValue, setGeneratingValue] = useState(false);
  
  // Deliverable types
  const types = [
    { id: 'note', label: 'Juridisk notat', icon: FileText },
    { id: 'contract', label: 'Kontrakt', icon: FileText },
    { id: 'letter', label: 'Brev', icon: Mail },
    { id: 'memo', label: 'Memo', icon: FileText },
    { id: 'analysis', label: 'Analyse', icon: Sparkles },
    { id: 'strategy', label: 'Strateginotat', icon: FileText }
  ];
  
  // Delivery channels
  const channels = [
    { id: 'save', label: 'Lagre i sak', description: 'Kun lagre til leveranser' },
    { id: 'email', label: 'Send til klient', description: 'Lag e-post med leveransen' },
    { id: 'portal', label: 'Klientportal', description: 'Publiser til klientportal' }
  ];
  
  // Generate AI-suggested value receipt
  const generateValueReceipt = async () => {
    if (!content) return;
    
    setGeneratingValue(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/ai/generate-value-receipt`,
        {
          content: content.substring(0, 2000), // Limit content length
          deliverable_type: deliverableType,
          case_name: caseName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.value_receipt) {
        setValueReceipt(response.data.value_receipt);
      }
    } catch (error) {
      console.error('Error generating value receipt:', error);
      // Fallback to a template
      const templates = {
        note: 'Juridisk vurdering som gir klar veiledning og reduserer usikkerhet rundt saken.',
        contract: 'Skreddersydd avtale som beskytter klientens interesser og sikrer tydelige vilkår.',
        letter: 'Profesjonell kommunikasjon som fremmer klientens posisjon.',
        memo: 'Intern oppsummering som effektiviserer videre arbeid på saken.',
        analysis: 'Grundig analyse som gir innsikt og grunnlag for gode beslutninger.',
        strategy: 'Strategisk veiledning som øker sannsynligheten for ønsket utfall.'
      };
      setValueReceipt(templates[deliverableType] || templates.note);
    } finally {
      setGeneratingValue(false);
    }
  };
  
  // Auto-suggest title from content
  useEffect(() => {
    if (isOpen && content && !title) {
      const firstLine = content.split('\n')[0]?.replace(/^#+\s*/, '').trim();
      if (firstLine && firstLine.length < 60) {
        setTitle(firstLine);
      } else {
        setTitle(`${caseName || 'Sak'} - ${types.find(t => t.id === deliverableType)?.label || 'Notat'}`);
      }
    }
  }, [isOpen, content, caseName, deliverableType]);
  
  // Handle save
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Skriv inn en tittel');
      return;
    }
    
    setSaving(true);
    try {
      const token = auth.getToken();
      
      // Save as deliverable with value receipt
      const response = await axios.post(
        `${API}/ai-workspace/deliverables`,
        {
          run_id: messageId,
          matter_id: caseId,
          title: title.trim(),
          deliverable_type: deliverableType,
          value_receipt: valueReceipt.trim() || null,
          delivery_channel: deliveryChannel,
          tags: tags
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Leveranse lagret!');
      onSaved?.(response.data);
      onClose();
      
      // If email channel, could trigger email composer
      if (deliveryChannel === 'email') {
        toast.info('Åpner e-postkomponist...', { duration: 2000 });
      }
      
    } catch (error) {
      console.error('Error saving deliverable:', error);
      toast.error('Kunne ikke lagre leveranse');
    } finally {
      setSaving(false);
    }
  };
  
  // Add tag
  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };
  
  // Remove tag
  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Bookmark className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Lagre som leveranse</h2>
                <p className="text-sm text-gray-500">Arkiver AI-output som formell leveranse</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tittel
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="F.eks. Juridisk vurdering av arbeidsforhold"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>
            
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Type leveranse
              </label>
              <div className="grid grid-cols-3 gap-2">
                {types.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setDeliverableType(type.id)}
                    className={cn(
                      "flex flex-col items-center gap-1 p-3 rounded-lg border transition-all text-center",
                      deliverableType === type.id
                        ? "border-violet-500 bg-violet-50 text-violet-700"
                        : "border-gray-200 hover:border-gray-300 text-gray-600"
                    )}
                  >
                    <type.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Value Receipt */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Verdi levert
                  <span className="ml-1 text-xs text-gray-400">(synlig på faktura)</span>
                </label>
                <button
                  onClick={generateValueReceipt}
                  disabled={generatingValue}
                  className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700"
                >
                  {generatingValue ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Sparkles className="w-3 h-3" />
                  )}
                  AI-forslag
                </button>
              </div>
              <textarea
                value={valueReceipt}
                onChange={(e) => setValueReceipt(e.target.value)}
                placeholder="Beskriv verdien denne leveransen gir klienten..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Hjelper med verdibasert prising og synliggjør arbeidet for klienten.
              </p>
            </div>
            
            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tagger
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                  >
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTag()}
                  placeholder="Legg til tag..."
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
                />
                <Button size="sm" variant="outline" onClick={addTag}>
                  <Tag className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Delivery Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Leveringskanal
              </label>
              <div className="space-y-2">
                {channels.map(channel => (
                  <button
                    key={channel.id}
                    onClick={() => setDeliveryChannel(channel.id)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                      deliveryChannel === channel.id
                        ? "border-violet-500 bg-violet-50"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                      deliveryChannel === channel.id
                        ? "border-violet-500"
                        : "border-gray-300"
                    )}>
                      {deliveryChannel === channel.id && (
                        <div className="w-2 h-2 bg-violet-500 rounded-full" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{channel.label}</p>
                      <p className="text-xs text-gray-500">{channel.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
            <Button variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !title.trim()}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Lagre leveranse
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SaveDeliverableModal;
